<script setup>
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import LinkCell from '@js/components/tables/cells/LinkCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import PriceCell from '@js/components/tables/cells/PriceCell.vue';
import StatusCell from '@js/components/tables/cells/StatusCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { type = undefined } = defineProps({
    type: {
        required: false,
        type: String,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    setGlobalFilter,
    availableFilters,
    filters,
    setFilters,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'name',
            customHeader: true,
            header: () => 'Nom',
            cell: (props) =>
                h(LinkCell, {
                    text: props.row.original.name,
                    href:
                        route(
                            'front.projectManagement.customer.updateCustomer',
                            {
                                tenant: props.row.original.id,
                            },
                        ) + '?tab=services',
                    target: '_self',
                    inertia: true,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'seo',
            customHeader: true,
            header: () => 'SEO',
            cell: (props) =>
                props.row.original.seoPackage ||
                props.row.original.seoHoursPerMonth !== null
                    ? h(NumberCell, {
                          number:
                              props.row.original.seoHoursPerMonth !== null
                                  ? props.row.original.seoHoursPerMonth
                                  : props.row.original.seoPackage.hoursPerMonth,
                          suffix: 'h',
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'netlinking',
            customHeader: true,
            header: () => 'Netlinking',
            cell: (props) =>
                props.row.original.seoPackage ||
                props.row.original.seoNetlinkingBudgetPerMonth !== null
                    ? h(PriceCell, {
                          price:
                              props.row.original.seoNetlinkingBudgetPerMonth !==
                              null
                                  ? props.row.original
                                        .seoNetlinkingBudgetPerMonth
                                  : props.row.original.seoPackage
                                        .netlinkingBudgetPerMonth,
                          withDecimals: false,
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'sea',
            customHeader: true,
            header: () => 'SEA',
            cell: (props) =>
                props.row.original.seaPackage ||
                props.row.original.seaHoursPerMonth !== null
                    ? h(NumberCell, {
                          number:
                              props.row.original.seaHoursPerMonth !== null
                                  ? props.row.original.seaHoursPerMonth
                                  : props.row.original.seaPackage.hoursPerMonth,
                          suffix: 'h',
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'smo',
            customHeader: true,
            header: () => 'SMO',
            cell: (props) =>
                props.row.original.smoPackage ||
                props.row.original.smoHoursPerMonth !== null
                    ? h(NumberCell, {
                          number:
                              props.row.original.smoHoursPerMonth !== null
                                  ? props.row.original.smoHoursPerMonth
                                  : props.row.original.smoPackage.hoursPerMonth,
                          suffix: 'h',
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'sma',
            customHeader: true,
            header: () => 'SMA',
            cell: (props) =>
                props.row.original.smaPackage ||
                props.row.original.smaHoursPerMonth !== null
                    ? h(NumberCell, {
                          number:
                              props.row.original.smaHoursPerMonth !== null
                                  ? props.row.original.smaHoursPerMonth
                                  : props.row.original.smaPackage.hoursPerMonth,
                          suffix: 'h',
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'devPackageId',
            customHeader: true,
            header: () => 'DEV',
            cell: (props) =>
                props.row.original.devPackage
                    ? h(TextCell, {
                          text: props.row.original.devPackage.name,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'hostingPackageId',
            customHeader: true,
            header: () => 'Hébergement',
            cell: (props) =>
                props.row.original.hostingPackage
                    ? h(TextCell, {
                          text: props.row.original.hostingPackage.name,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'cloudflarePackageId',
            customHeader: true,
            header: () => 'Cloudflare',
            cell: (props) =>
                props.row.original.cloudflarePackage
                    ? h(TextCell, {
                          text: props.row.original.cloudflarePackage.name,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'shoppingfeedPackageId',
            customHeader: true,
            header: () => 'Shoppingfeed',
            cell: (props) =>
                props.row.original.shoppingfeedPackage
                    ? h(TextCell, {
                          text: props.row.original.shoppingfeedPackage.name,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'special',
            customHeader: true,
            header: () => 'Spécial',
            cell: (props) =>
                props.row.original.performanceStagePackage ||
                props.row.original.specialPackage
                    ? h(TextCell, {
                          text: props.row.original.performanceStagePackage
                              ? props.row.original.performanceStagePackage.name
                              : props.row.original.specialPackage.name,
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'emailing',
            customHeader: true,
            header: () => 'Emailing',
            cell: (props) =>
                h(StatusCell, {
                    status: props.row.original.emailing,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'dev',
            customHeader: true,
            header: () => 'DEV',
            cell: (props) =>
                h(StatusCell, {
                    status: props.row.original.dev,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'devCoefficient',
            customHeader: true,
            header: () => 'Coef dev',
            cell: (props) =>
                props.row.original.devCoefficient
                    ? h(NumberCell, {
                          number: props.row.original.devCoefficient,
                          withDecimals:
                              props.row.original.devCoefficient !== 1.0,
                      })
                    : null,
        }),
    ],
    url: route('front.projectManagement.customer.postServices'),
    data: {
        type,
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :set-global-filter="setGlobalFilter"
        :available-filters="availableFilters"
        :filters="filters"
        :set-filters="setFilters"
        :loading="loading"
    />
</template>
